"use client";

import { cn } from "@/utils/cn";
import React from "react";
import { Input as AriaInput, Label, Text, TextField } from "react-aria-components";
import { useController, type Control, type FieldValues, type Path, type RegisterOptions } from "react-hook-form";
import { ArrayInput } from "../rac/array-input";
import { ButtonGroup } from "../rac/button-group";
import { CheckboxGroupInput } from "../rac/checkbox-group-input";
import { CurrencyField } from "../rac/currency-input";
import { JollyDatePicker, JollyDateRangePicker } from "../rac/date-picker";
import { RadioGroupInput } from "../rac/radio-group-input";
import { SelectInput } from "../rac/select-input";
import { SelectInput as SelectInput2 } from "../rac/select-input2";
import { SwitchInput } from "../rac/switch-input";
import { TextAreaInput } from "../rac/text-area-input";
import { normalizeDate, type InputDate, type InputRange } from "./normalize-date";

interface FallbackInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	label?: React.ReactNode;
	name?: string;
	description?: React.ReactNode;
	errorMessage?: React.ReactNode;
	className?: string;
	inputClassName?: string;
	labelClassName?: string;
}

export const FallbackInput = React.forwardRef<HTMLInputElement, FallbackInputProps>(
	({ label, name, description, errorMessage, className, inputClassName, labelClassName, ...props }, ref) => {
		return (
			<TextField className={cn("flex flex-col gap-1", className)}>
				{label && (
					<Label htmlFor={name} className={cn("text-title-medium", labelClassName)}>
						{label}
					</Label>
				)}

				<AriaInput
					id={name}
					ref={ref}
					{...props}
					value={props.value ?? ""}
					className={cn(
						"border px-3 py-2.5 h-10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-600",
						errorMessage ? "border-primary-600" : "border-gray-300",
						inputClassName,
					)}
				/>

				{description && (
					<Text slot="description" className="text-xs text-muted-foreground">
						{description}
					</Text>
				)}

				{errorMessage && (
					<Text slot="errorMessage" className="text-sm font-medium text-destructive">
						{errorMessage}
					</Text>
				)}
			</TextField>
		);
	},
);
FallbackInput.displayName = "FallbackInput";

interface ErrorBoundaryProps {
	children: React.ReactNode;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error) {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		if (!errorInfo) return;
		if (error) throw error;
		// Log.fatal("ErrorBoundary caught an error", {
		// error: error.toString(),
		// errorInfo: errorInfo.componentStack,
		// });
	}

	render() {
		if (this.state.hasError && this.state.error) {
			return <h1>{this.state.error.toString()}</h1>;
		}

		return this.props.children;
	}
}

const racTypeToComponent: Record<string, React.ElementType> = {
	radio: RadioGroupInput,
	checkbox: CheckboxGroupInput,
	inr: CurrencyField,
	buttongroup: ButtonGroup,
	date: JollyDatePicker,
	daterange: JollyDateRangePicker,
	select: SelectInput,
	textarea: TextAreaInput,
	array: ArrayInput,
	switch: SwitchInput,
	select2: SelectInput2,
};

const typeMap = new Map([
	["array", "array"],
	["inr", "inr"],
	["number", "number"],
	["radio", "radio"],
	["checkbox", "checkbox"],
	["switch", "switch"],
	["buttongroup", "buttongroup"],
	["date", "date"],
	["daterange", "daterange"],
	["select", "select"],
	["textarea", "textarea"],
	["email", "email"],
	["select", "select"],
	["select2", "select2"],
]);

const normalizeInputType = (type: string = "text"): string => {
	return typeMap.get(type.toLowerCase()) || type.toLowerCase() || "text";
};

type Option = { label: string; value: unknown } | { label: string; value: unknown; options: Option[] };

interface InputProps<T extends FieldValues = FieldValues> {
	type?: string;
	name?: Path<T>;
	control?: Control<T>;
	rules?: RegisterOptions<T>;
	options?: Option[];
	markFieldDirty?: (fieldName: string) => void; // Hack for manual dirty tracking
	[key: string]: unknown;
}

export const Input = <T extends FieldValues = FieldValues>({ type = "text", control, ...props }: InputProps<T>) => {
	const normalizedType = normalizeInputType(type);

	const Component = racTypeToComponent[normalizedType] || FallbackInput;

	if (control) return <RHFInput<T> type={normalizedType} control={control} Component={Component} {...props} />;

	return (
		<ErrorBoundary>
			<Component type={normalizedType} {...props} />
		</ErrorBoundary>
	);
};

const normalizedValueFunc = (value: unknown, type: string): unknown => {
	if (type === "date" || type === "daterange") {
		return normalizeDate(value as InputDate | InputRange);
	}
	// For text-based inputs, ensure we never return null/undefined
	if (type === "text" || type === "email" || type === "textarea") {
		return value ?? "";
	}
	// For number inputs, ensure we return empty string instead of null/undefined
	if (type === "number") {
		return value ?? "";
	}
	return value ?? "";
};

export const parsedValueFunc = (e: unknown, type: string): unknown => {
	switch (type) {
		case "switch":
			return e;
		case "number": {
			const target = (e as { target: { value: string } }).target;
			return target.value === "" ? undefined : Number(target.value);
		}
		case "text":
		case "email":
		case "checkbox":
		case "radio":
		case "inr":
		case "buttongroup":
		case "array":
		case "select":
		case "select2":
			return e;
		case "date":
		case "daterange":
			return normalizeDate(e as InputDate | InputRange);
		case "textarea": {
			const target = (e as { target?: { value: string } }).target;
			return target?.value || e;
		}
		default: {
			const target = (e as { target: { value: string } }).target;
			return target.value;
		}
	}
};

interface RHFInputProps<T extends FieldValues = FieldValues> {
	type: string;
	name?: Path<T>;
	control: Control<T>;
	rules?: RegisterOptions<T>;
	Component: React.ElementType;
	markFieldDirty?: (fieldName: string) => void; // Hack for manual dirty tracking
	[key: string]: unknown;
}

function RHFInput<T extends FieldValues = FieldValues>({
	type,
	name,
	control,
	rules,
	Component,
	markFieldDirty,
	...rest
}: RHFInputProps<T>) {
	// Type guard to ensure name is defined for useController
	if (!name) {
		throw new Error("RHFInput requires a name prop when used with control");
	}

	const {
		field: { onChange, onBlur, value },
		fieldState: { error },
	} = useController({ name: name as Path<T>, control, rules });

	const handleChange = (e: unknown) => {
		const parsedValue = parsedValueFunc(e, type);
		if (value !== parsedValue) {
			onChange(parsedValue);
			// Hack: manually mark field as dirty since React Hook Form isn't working
			if (markFieldDirty && name) {
				markFieldDirty(name);
			}
		}
	};

	const normalizedValue = normalizedValueFunc(value, type);

	return (
		<ErrorBoundary>
			<Component
				type={type}
				value={normalizedValue}
				onChange={handleChange}
				onBlur={onBlur}
				errorMessage={error?.message}
				{...rest}
			/>
		</ErrorBoundary>
	);
}
